import type { User } from '../../../authentication/types/types.auth';
import { Lock, Public, Save } from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { motion } from 'framer-motion';
import React from 'react';
import { ColorPicker } from '@shared/components/ColorPicker';
import { ProfileFormField } from './ProfileFormField';

const AVATAR_COLORS = [
  { color: '#0b74da', name: 'Cornflower Blue' },
  { color: '#78bbf1', name: 'Sky Blue' },
  { color: '#2E5AAC', name: 'Navy Blue' },
  { color: '#8B5CF6', name: 'Purple' },
  { color: '#EC4899', name: 'Pink' },
  { color: '#F59E0B', name: 'Amber' },
  { color: '#10B981', name: 'Emerald' },
  { color: '#EF4444', name: 'Red' },
  { color: '#64748B', name: 'Slate' },
  { color: '#06B6D4', name: 'Cyan' },
  { color: '#F97316', name: 'Orange' },
  { color: '#84CC16', name: 'Lime' },
  { color: '#A855F7', name: 'Violet' },
  { color: '#14B8A6', name: 'Teal' },
  { color: '#F43F5E', name: 'Rose' },
  { color: '#8B5A3C', name: 'Brown' },
];

interface ProfileFormData {
  avatarColor: string;
  firstName: string;
  hourlyRate: string;
  lastName: string;
  username: string;
  email: string;
  company: string;
  positionTitle: string;
  industry: string;
  companySize: string;
  tagline: string;
  showDealList: boolean;
}

interface ProfileDetailsCardProps {
  user: User;
  isEditing: boolean;
  saving: boolean;
  formData: ProfileFormData;
  onFieldChange: (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  onColorChange: (color: string) => void;
  onToggleChange: (field: string) => (value: boolean) => void;
  onSave: () => void;
  onCancel: () => void;
}

const SECTION_ICON_SX = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  borderRadius: 2,
  background: '#eef6ff',
  border: '1px solid rgba(120, 187, 241, 0.4)',
  color: '#0b74da',
} as const;

const SECTION_HEADER_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  mb: 1,
  pb: 2,
  borderBottom: '2px solid',
  borderColor: '#0b74da',
} as const;

const LABEL_SX = {
  mb: 1.5,
  fontWeight: 700,
  fontSize: '0.6875rem',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#475569',
} as const;

const READONLY_BOX_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  px: 2,
  py: 1.5,
  borderRadius: 2,
  backgroundColor: '#f7f9fb',
  border: '1px solid #e5e9ee',
} as const;

export const ProfileDetailsCard: React.FC<ProfileDetailsCardProps> = ({
  user,
  isEditing,
  saving,
  formData,
  onFieldChange,
  onColorChange,
  onToggleChange,
  onSave,
  onCancel,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2, duration: 0.5 }}
    >
      <div className="profile-details-card">
        <div className="profile-details-card__header">
          <h3 className="profile-details-card__title">
            {isEditing ? 'Edit Profile Information' : 'Profile Information'}
          </h3>

          {isEditing && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={onCancel}
                variant="outlined"
                disabled={saving}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button
                onClick={onSave}
                variant="contained"
                disabled={saving}
                startIcon={<Save />}
                data-testid="save-profile-button"
                sx={{
                  'background': '#0b74da',
                  'borderRadius': 2,
                  'textTransform': 'none',
                  'fontWeight': 600,
                  'boxShadow': '0 4px 14px rgba(11, 116, 218, 0.25)',
                  '&:hover': {
                    background: '#095fae',
                    boxShadow: '0 6px 20px rgba(11, 116, 218, 0.35)',
                    transform: 'translateY(-1px)',
                  },
                  'transition': 'all 0.2s ease',
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          )}
        </div>

        <Divider sx={{ mb: 4 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <Box>
            <Box sx={SECTION_HEADER_SX}>
              <Box sx={SECTION_ICON_SX}>
                <Public sx={{ fontSize: 22 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.0625rem', color: '#0f1419' }}>
                Public Information
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: '#475569', mb: 3, fontStyle: 'italic' }}>
              This information will be displayed on your reviews and in the user directory
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)' } }}>
                  <ProfileFormField
                    label="Username"
                    value={formData.username}
                    displayValue={user.username}
                    isEditing={isEditing}
                    onChange={onFieldChange('username')}
                    placeholder="e.g., john_doe"
                    dataTestId="username-input"
                  />
                </Box>

                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)' } }}>
                  <ProfileFormField
                    label="Tag Line"
                    value={formData.tagline}
                    displayValue={user.tagLine}
                    isEditing={isEditing}
                    onChange={onFieldChange('tagline')}
                    placeholder="e.g., Closing major accounts in data analytics since 2018"
                    dataTestId="tagline-input"
                    renderDisplay={() => (
                      <Typography variant="body1" sx={{ fontWeight: 500, fontStyle: 'italic' }}>
                        {user.tagLine || '—'}
                      </Typography>
                    )}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)' } }}>
                  {isEditing
                    ? (
                        <ColorPicker
                          value={formData.avatarColor}
                          onChange={onColorChange}
                          label="Avatar Color"
                        />
                      )
                    : (
                        <Box>
                          <Typography variant="body2" sx={LABEL_SX}>
                            Avatar Color
                          </Typography>
                          <Box sx={READONLY_BOX_SX}>
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: 1,
                                backgroundColor: user.avatarColor || '#0b74da',
                                border: '2px solid white',
                                boxShadow: '0 2px 6px rgba(15,20,25,0.12)',
                                flexShrink: 0,
                              }}
                            />
                            <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.9375rem', color: '#0f1419' }}>
                              {AVATAR_COLORS.find(c => c.color === (user.avatarColor || '#0b74da'))?.name || 'Custom'}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                </Box>

                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)' } }}>
                  <ProfileFormField
                    label="Hourly Rate (USD)"
                    value={formData.hourlyRate}
                    displayValue={user.hourlyRate ? `$${user.hourlyRate}` : '—'}
                    isEditing={isEditing}
                    onChange={onFieldChange('hourlyRate')}
                    placeholder="e.g., 150"
                    dataTestId="hourly-rate-input"
                    type="number"
                    helperText="We recommend keeping it around $300 USD/hour or less to maximize your chances of connecting with buyers."
                  />
                </Box>
              </Box>

              <Box>
                <Typography variant="body2" sx={LABEL_SX}>
                  Show Deal List
                </Typography>
                <Tooltip
                  title={!isEditing ? 'To change this preference, click the edit button first' : ''}
                  arrow
                  placement="top"
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 2,
                      py: 1.5,
                      borderRadius: 2,
                      border: '1px solid #e5e9ee',
                      backgroundColor: '#f7f9fb',
                      cursor: !isEditing ? 'not-allowed' : 'default',
                      opacity: !isEditing ? 0.7 : 1,
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: '#0f1419' }}>
                        Display deal list on profile
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#475569' }}>
                        {formData.showDealList
                          ? 'Users will be able to see a consolidated list of your published deals reviews'
                          : 'Users will not be able to see a consolidated list of your published deals reviews, but they will still see individual reviews'}
                      </Typography>
                    </Box>
                    <Switch
                      checked={formData.showDealList}
                      onChange={e => onToggleChange('showDealList')(e.target.checked)}
                      disabled={!isEditing}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#0b74da' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#0b74da',
                        },
                      }}
                    />
                  </Box>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          <Box>
            <Box sx={SECTION_HEADER_SX}>
              <Box sx={SECTION_ICON_SX}>
                <Lock sx={{ fontSize: 22 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.0625rem', color: '#0f1419' }}>
                Private Information
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: '#475569', mb: 3, fontStyle: 'italic' }}>
              This information will always be private. No one else can see it.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)' } }}>
                  <ProfileFormField
                    label="First Name"
                    value={formData.firstName}
                    displayValue={user.firstName}
                    isEditing={false}
                    onChange={onFieldChange('firstName')}
                  />
                </Box>

                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)' } }}>
                  <ProfileFormField
                    label="Last Name"
                    value={formData.lastName}
                    displayValue={user.lastName}
                    isEditing={false}
                    onChange={onFieldChange('lastName')}
                  />
                </Box>
              </Box>

              <ProfileFormField
                label="Email"
                value={formData.email}
                displayValue={user.email}
                isEditing={false}
                onChange={onFieldChange('email')}
                type="email"
              />

              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)' } }}>
                  <ProfileFormField
                    label="Current Company"
                    value={formData.company}
                    displayValue={user.company}
                    isEditing={isEditing}
                    onChange={onFieldChange('company')}
                    dataTestId="company-input"
                  />
                </Box>

                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)' } }}>
                  <ProfileFormField
                    label="Position Title"
                    value={formData.positionTitle}
                    displayValue={user.positionTitle}
                    isEditing={isEditing}
                    onChange={onFieldChange('positionTitle')}
                    dataTestId="position-input"
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </div>
    </motion.div>
  );
};
